import type { EmployeurDTO } from '@/features/api/extranet/dto/Demande/AF'
import { addPreBuilder, type Flag, type Regles } from '@/features/domaine/moteur-regles'
import { LevelRegleEnum } from '@/features/domaine/types/opco/shared'

import type { OutilFpeDTO, PrestationDTO } from '@/features/api/extranet/dto/Demande/OUTILFPE'
import type { FichierDTO, FraisDTO } from '@/features/api/extranet/dto/Demande/shared'
import type { EtablissementAdherentDTO } from '@/features/api/extranet/dto/shared'
import { isNotNullOrUndefined, isNotNullUndefinedOrEmptyString } from '@/features/shared/tools'
import { PropertyStatus } from '../../property-status'
import {
  isAdherentOuMandatGestion,
  rgEstMailFactory,
  rgEstMaxLengthFactory,
  rgEstNumeric,
  rgEstNumericMaxFactory,
  rgEstNumericMinFactory,
  rgEstNumTelFactory,
  rgEstObligatoireFactory,
  rgEstVraiFactory,
} from '../shared'
import { SAISIE_OBLIGATOIRE } from '../shared/messages-erreurs'
import type { ContextReglesOutilFpe, ReglesOutilFpe } from './types'

// /* Initalisation des helpers */
const addPre = addPreBuilder<OutilFpeDTO, ContextReglesOutilFpe>()

/* Declaration de type pour les sous modeles */
type RegleOutilFpeMembre<T extends object> = Regles<T, ContextReglesOutilFpe, LevelRegleEnum, Flag, OutilFpeDTO>

/* Etablissement */
const reglesEtablissementEmployeur: RegleOutilFpeMembre<EtablissementAdherentDTO> = {}

/* Employeur */
const reglesEmployeur: RegleOutilFpeMembre<EmployeurDTO> = {
  etablissement: reglesEtablissementEmployeur,

  contact: {
    email: {
      ...rgEstMaxLengthFactory(200),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.employeur?.contact?.email?.$status === PropertyStatus.Required,
        rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      ),
      ...rgEstMailFactory(LevelRegleEnum.Metier),
    },
    telephone: {
      ...rgEstMaxLengthFactory(10),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.employeur?.contact?.telephone?.$status === PropertyStatus.Required,
        rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      ),
      ...rgEstNumTelFactory(LevelRegleEnum.Metier),
    },
  },
}

/* Fichiers */
const reglesFichiers: RegleOutilFpeMembre<FichierDTO> = {}

/* Formation */
const reglesPrestation: RegleOutilFpeMembre<PrestationDTO> = {
  etablissement: {
    siret: {
      valeur: {
        ...addPre(
          (_value, _parent, _model, context) =>
            context.status.prestation?.etablissement?.$status === PropertyStatus.Required,
          rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        ),
      },
    },
  },
  fraisAnnexes: {
    montant: {
      ...addPre<FraisDTO>((_value, parent, model, context) => {
        const index = model.prestation?.fraisAnnexes?.indexOf(parent) ?? -1
        return context.status?.prestation?.fraisAnnexes?.at(index)?.montant?.$status === PropertyStatus.Required
      }, rgEstObligatoireFactory(LevelRegleEnum.Transmettre)),
      ...addPre<FraisDTO>(
        (_value, parent, model, context) => {
          const index = model.prestation?.fraisAnnexes?.indexOf(parent) ?? -1
          return context.status?.prestation?.fraisAnnexes?.at(index)?.montant?.$status !== PropertyStatus.NotPresent
        },
        {
          ...rgEstNumeric,
          ...rgEstNumericMaxFactory(100000, LevelRegleEnum.Technique, 'Le montant doit être inférieur à 100000'),
          ...rgEstNumericMinFactory(0, LevelRegleEnum.Technique, 'Le montant doit être supérieur à 0'),
        },
      ),
    },
    quantite: {
      ...addPre<FraisDTO>((_value, parent, model, context) => {
        const index = model.prestation?.fraisAnnexes?.indexOf(parent) ?? -1
        return context.status?.prestation?.fraisAnnexes?.at(index)?.quantite?.$status === PropertyStatus.Required
      }, rgEstObligatoireFactory(LevelRegleEnum.Transmettre)),
      ...addPre<FraisDTO>(
        (_value, parent, model, context) => {
          const index = model.prestation?.fraisAnnexes?.indexOf(parent) ?? -1
          return context.status?.prestation?.fraisAnnexes?.at(index)?.quantite?.$status !== PropertyStatus.NotPresent
        },
        {
          ...rgEstNumeric,
          ...rgEstNumericMaxFactory(5001, LevelRegleEnum.Technique, 'La quantité doit être inférieure à 5001'),
          ...rgEstNumericMinFactory(0, LevelRegleEnum.Technique, 'La quantité doit être supérieure à 0'),
        },
      ),
    },
  },
  $model: {
    'etablissement-prestataire-obligatoire': {
      validateur: (model) => {
        if (isNotNullOrUndefined(model.etablissement)) return
        return { etablissement: SAISIE_OBLIGATOIRE }
      },
      level: LevelRegleEnum.Transmettre,
    },
    'etablissement-prestataire-actif': {
      pre: (model) => isNotNullUndefinedOrEmptyString(model.etablissement?.etatAdministratif),
      validateur: (model) => {
        if (model.etablissement!.etatAdministratif === 'A') return
        return {
          etablissement:
            'Vous ne pouvez pas sélectionner un établissement inactif, merci de sélectionner un autre établissement',
        }
      },
      level: LevelRegleEnum.Technique,
    },
  },
}

/* REGLES  ------------------------------------------------------------------------ */
export const reglesConfiguration: ReglesOutilFpe = {
  attestationPiecesJustificatives: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.attestationPiecesJustificatives?.$status === PropertyStatus.Required,
      {
        ...rgEstVraiFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  attestationSurHonneur: {
    ...rgEstVraiFactory(LevelRegleEnum.Transmettre),
  },
  acceptationCg: {
    ...rgEstVraiFactory(LevelRegleEnum.Transmettre),
  },

  employeur: reglesEmployeur,
  prestation: reglesPrestation,
  fichiers: reglesFichiers,

  $model: {
    controleFichier: {
      validateur: (model, _globalModel, context) => {
        if (!isAdherentOuMandatGestion(context.typeContext, false)) return

        for (const doc of context.naturesFichiers.obligatoires) {
          if (model.fichiers?.some((x) => x.nature === doc) !== true) return { fichiers: false }
        }
      },
      message: SAISIE_OBLIGATOIRE,
      level: LevelRegleEnum.Transmettre,
    },
  },
} as const
